import React, { useEffect, useRef } from 'react';

// Theme tokens so the confetti adapts to light/dark. CSS vars work here because the
// pieces are appended into the component's own (themed) DOM subtree.
const DEFAULT_COLORS = [
    'var(--malachite-success-background)',
    'var(--malachite-text-success)',
    'var(--malachite-tag-primary-foreground)',
    'var(--malachite-warning-background)',
    'var(--malachite-error-background)',
];

/** Joins the palette into a content key; NUL cannot occur in a CSS colour value. */
const COLORS_KEY_SEPARATOR = '\u0000';

/**
 * `window.matchMedia` is absent under SSR and in older test environments. When it cannot
 * be consulted, assume reduced motion — skipping a decorative burst is the safe default.
 */
const prefersReducedMotion = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return true;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export interface ConfettiProps {
    /** Number of confetti pieces. */
    count?: number;
    /** Colors cycled across the pieces. */
    colors?: string[];
    className?: string;
}

// Dependency-free confetti burst via the Web Animations API. Uses WAAPI (not Tailwind
// transform utilities) so it works inside the shadow DOM, and respects
// prefers-reduced-motion. Fires once on mount.
const Confetti: React.FC<ConfettiProps> = ({
    count = 150,
    colors = DEFAULT_COLORS,
    className = '',
}) => {
    const ref = useRef<HTMLDivElement>(null);

    // Keyed on the palette's contents, not the array's identity: an inline
    // `colors={['red', 'blue']}` is a new array every render and would re-fire the burst.
    const colorsKey = colors.join(COLORS_KEY_SEPARATOR);

    useEffect(() => {
        const container = ref.current;
        if (!container) return;
        if (prefersReducedMotion()) return;

        const palette = colorsKey.split(COLORS_KEY_SEPARATOR);

        const pieces = Array.from({ length: count }, (_, i) => {
            const piece = document.createElement('div');
            const size = 10 + ((i * 7) % 10);
            piece.style.cssText = `position:absolute;top:-5%;left:${(i * 37) % 100}%;width:${size}px;height:${Math.round(size * 0.5)}px;background:${palette[i % palette.length]};border-radius:2px;`;
            container.appendChild(piece);

            const xDrift = (((i * 53) % 100) - 50) * 4;
            const rotation = 360 + ((i * 47) % 720);
            piece.animate(
                [
                    { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
                    {
                        transform: `translate(${xDrift}px, 105vh) rotate(${rotation}deg)`,
                        opacity: 1,
                        offset: 0.9,
                    },
                    { transform: `translate(${xDrift}px, 110vh) rotate(${rotation}deg)`, opacity: 0 },
                ],
                {
                    duration: 2600 + ((i * 17) % 1600),
                    delay: (i * 11) % 500,
                    easing: 'cubic-bezier(0.2, 0.6, 0.4, 1)',
                    fill: 'forwards',
                },
            );
            return piece;
        });

        return () => {
            for (const piece of pieces) piece.remove();
        };
    }, [count, colorsKey]);

    return (
        <div
            ref={ref}
            aria-hidden
            className={`pointer-events-none fixed inset-0 overflow-hidden z-50 ${className}`}
        />
    );
};

export default Confetti;
export { Confetti };
